import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { AnyValue } from "@/lib/rowTypes";

/**
 * Session guard utilities used by write paths that depend on `auth.uid()`
 * matching the row `user_id` (RLS `WITH CHECK`). When the JWT becomes
 * malformed/expired ("missing sub claim" 403s on /user), inserts into
 * `topic_mastery`, `lesson_progress`, and `learning_event_ledger` all fail
 * silently. This module gives callers a way to detect that class of error,
 * force a token refresh, and either retry or notify the user.
 */

export function isRlsOrAuthError(error: AnyValue): boolean {
  if (!error) return false;
  const e = error as { code?: string; message?: string; status?: number };
  if (e.code === "42501") return true;
  const msg = (e.message || "").toLowerCase();
  if (msg.includes("row-level security") || msg.includes("row level security")) return true;
  if (msg.includes("jwt") || msg.includes("missing sub claim")) return true;
  if (e.status === 401 || e.status === 403) return true;
  return false;
}

let refreshInFlight: Promise<boolean> | null = null;
let lastToastAt = 0;

function notifyExpired() {
  const now = Date.now();
  if (now - lastToastAt < 8000) return;
  lastToastAt = now;
  try {
    toast.error("Your session expired. Please sign in again.");
  } catch {
    // no-op
  }
}

/**
 * Ensure the local session has a live JWT. Returns true if the session is
 * usable after this call. On unrecoverable failure, signs the user out
 * locally so route guards send them back to /login.
 */
export async function assertFreshSession(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    try {
      const { data, error } = await supabase.auth.getSession();
      const session = data?.session ?? null;
      const expiresAt = session?.expires_at ?? 0;
      const nowSec = Math.floor(Date.now() / 1000);
      const needsRefresh = !session || !!error || expiresAt - nowSec < 30;
      if (!needsRefresh) return true;

      const { data: refreshed, error: refreshError } = await supabase.auth.refreshSession();
      if (refreshError || !refreshed?.session) {
        console.warn("[sessionGuard] refresh failed:", refreshError?.message);
        notifyExpired();
        try { await supabase.auth.signOut({ scope: "local" }); } catch { /* noop */ }
        return false;
      }
      return true;
    } catch (err) {
      console.warn("[sessionGuard] assertFreshSession threw:", err);
      return false;
    } finally {
      // Clear in the next tick so concurrent callers share the same refresh.
      setTimeout(() => { refreshInFlight = null; }, 0);
    }
  })();
  return refreshInFlight;
}

/**
 * Run a write that may fail with an RLS/JWT error. On such failure, refresh
 * the session and retry once. Returns `{ ok, error }`.
 *
 * The `op` should return `{ error }` in the Supabase-style shape or throw.
 */
export async function runWithSessionRetry<T extends { error?: AnyValue } | void>(
  op: () => Promise<T>,
): Promise<{ ok: boolean; result: T | null; error: AnyValue }> {
  try {
    const first = await op();
    const firstErr = (first as AnyValue)?.error ?? null;
    if (!firstErr) return { ok: true, result: first, error: null };
    if (!isRlsOrAuthError(firstErr)) return { ok: false, result: first, error: firstErr };
    const refreshed = await assertFreshSession();
    if (!refreshed) return { ok: false, result: first, error: firstErr };
    const second = await op();
    const secondErr = (second as AnyValue)?.error ?? null;
    if (secondErr) {
      notifyExpired();
      return { ok: false, result: second, error: secondErr };
    }
    return { ok: true, result: second, error: null };
  } catch (err) {
    if (isRlsOrAuthError(err)) {
      const refreshed = await assertFreshSession();
      if (refreshed) {
        try {
          const retry = await op();
          const retryErr = (retry as AnyValue)?.error ?? null;
          if (!retryErr) return { ok: true, result: retry, error: null };
          return { ok: false, result: retry, error: retryErr };
        } catch (err2) {
          return { ok: false, result: null, error: err2 };
        }
      }
    }
    return { ok: false, result: null, error: err };
  }
}